import Zone from "@insite/client-framework/Components/Zone";
import { DropDownFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Block, { BlockProps, BlockVariant, BlockVariantSide } from "@store/Components/Common/Block";
import blockPreserveSpacingField from "@store/Components/Common/Block/fields/preserveSpacing";
import blockVariantField from "@store/Components/Common/Block/fields/variant";
import blockVariantSideField from "@store/Components/Common/Block/fields/variantSide";
import gridVerticalGapField, {
    getVerticalGapData,
    gridVerticalGapFieldOptions,
    VerticalGap,
} from "@store/fields/gridVerticalGap";
import { baseGridHiddenProps, baseGridProps, baseGridStyles } from "@store/theme/common/props/baseGrid";
import React from "react";
import { css } from "styled-components";
import isWebsiteAccessedByMobileApp from "@store/utilities/isWebsiteAccessedByMobileApp";

const enum SideColumnSide {
    Left = "Left",
    Right = "Right",
}

const enum fields {
    side = "side",
    hideForMobile = "hideForMobile",
    hideForMobileApp = "hideForMobileApp",
    blockPreserveSpacing = "blockPreserveSpacing",
    blockFullWidth = "blockFullWidth",
    blockVariant = "blockVariant",
    blockVariantSide = "blockVariantSide",
    gridVerticalGap = "gridVerticalGap",
}

interface Props extends WidgetProps {
    fields: {
        [fields.side]: string;
        [fields.hideForMobile]: boolean;
        [fields.hideForMobileApp]: boolean;
        [fields.blockPreserveSpacing]: boolean;
        [fields.blockFullWidth]: boolean;
        [fields.blockVariant]: BlockVariant;
        [fields.blockVariantSide]: BlockVariantSide;
        [fields.gridVerticalGap]: VerticalGap;
    };
}

const SideColumnRow: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { side, hideForMobile, hideForMobileApp, blockPreserveSpacing, blockVariant, blockVariantSide, gridVerticalGap },
        id,
    } = props;
    let _gridProps = baseGridProps;

    if (hideForMobile) {
        _gridProps = baseGridHiddenProps;
    } else if (gridVerticalGap) {
        _gridProps = {
            ...baseGridProps,
            container: {
                ...baseGridProps.container,
                css: css`
                    ${baseGridStyles(getVerticalGapData(gridVerticalGap))}
                `,
            },
        };
    }

    const isSideColumnLeft = side === SideColumnSide.Left;
    const isSideColumnRight = side === SideColumnSide.Right;
    const isHiddenForMobileApp = hideForMobileApp && isWebsiteAccessedByMobileApp();
    const isLeftColumnHidden = isSideColumnLeft && isHiddenForMobileApp;
    const isRightColumnHidden = isSideColumnRight && isHiddenForMobileApp;

    const gridProps = {
        container: _gridProps.container,
        leftColumn: isSideColumnLeft
            ? _gridProps.sideColumn
            : {
                ..._gridProps.mainColumn,
                width: isRightColumnHidden ? 12 : _gridProps.mainColumn?.width,
            },
        rightColumn: isSideColumnRight
            ? _gridProps.sideColumn
            : {
                ..._gridProps.mainColumn,
                width: isLeftColumnHidden ? 12 : _gridProps.mainColumn?.width,
            },
    };

    const blockProps: BlockProps = {
        preserveSpacing: blockPreserveSpacing,
        variant: blockVariant,
        variantSide: blockVariantSide,
    };

    return (
        <Block {...blockProps}>
            <GridContainer {...gridProps.container}>
                {!isLeftColumnHidden && (
                    <GridItem {...gridProps.leftColumn}>
                        <Zone contentId={id} zoneName="Content0" />
                    </GridItem>
                )}
                {!isRightColumnHidden && (
                    <GridItem {...gridProps.rightColumn}>
                        <Zone contentId={id} zoneName="Content1" />
                    </GridItem>
                )}
            </GridContainer>
        </Block>
    );
    /* Poolcorp Custom Code */
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: SideColumnRow,
    definition: {
        group: "Basic",
        icon: "Columns",
        fieldDefinitions: [
            {
                name: fields.side,
                displayName: "Side Column",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Left",
                        value: SideColumnSide.Left,
                    },
                    {
                        displayName: "Right",
                        value: SideColumnSide.Right,
                    },
                ],
                defaultValue: SideColumnSide.Left,
                fieldType: "General",
                sortOrder: 0,
                isRequired: true,
            },
            {
                name: fields.hideForMobile,
                displayName: "Hide side column for smaller devices",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.hideForMobileApp,
                displayName: "Hide side column for mobile app",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                ...blockVariantField,
                name: fields.blockVariant,
                sortOrder: 10,
                tab: advancedTab,
            },
            {
                ...blockVariantSideField,
                name: fields.blockVariantSide,
                sortOrder: 11,
                tab: advancedTab,
            },
            {
                ...gridVerticalGapField,
                name: fields.gridVerticalGap,
                tooltip: `${gridVerticalGapField.tooltip} (Default: \"Large\")`,
                options: gridVerticalGapFieldOptions.filter((x: any) => ![VerticalGap.Block].includes(x.value)),
                sortOrder: 12,
                tab: advancedTab,
                isVisible: (x: Props) => !x.fields.hideForMobile,
            } as DropDownFieldDefinition<string>,
            {
                ...blockPreserveSpacingField,
                name: fields.blockPreserveSpacing,
                sortOrder: 13,
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
